import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { FaUser } from "react-icons/fa";
import { FiLogOut } from "react-icons/fi";
import UserData from "../Controls/UserData";
import Helpers from "../Controls/helpers";
import ThemeToggle from "../Controls/ThemeToggle";
import ModeSwitch from "../Controls/ModeSwitch";
import useAppMode, { RECRUITER } from "../../mode/useAppMode";
import {
  getOpenSkillForums,
  closeSkillForum,
  setActiveSkillForum,
  subscribeSkillForum,
} from "../../skillForum/openForum";
import { useSessionShellQuery } from "../../query/useSessionShell";
import "../../style/scss/Recruiter.scss";
import "../../style/scss/SkillForum.scss";

export default function Menu({ updateLogin }) {
  const navigate = useNavigate();
  const isLoggedIn = !!window.sessionStorage.getItem("BearerToken");
  const { data: shell } = useSessionShellQuery({ enabled: isLoggedIn });
  // Prefer live shell; fall back to login HydrateSessionAsync cache until Query resolves.
  const userData = isLoggedIn ? shell || UserData.UserData() || {} : {};
  const showHiring = Helpers.canHire(userData);
  const showAdmin = Helpers.isSysAdmin(userData);
  const [mode] = useAppMode();
  const recruiterMode = showHiring && mode === RECRUITER;
  const [forums, setForums] = useState(() => getOpenSkillForums());
  useEffect(() => subscribeSkillForum(() => setForums(getOpenSkillForums())), []);

  function onLogout(e) {
    e.preventDefault();
    window.sessionStorage.clear();
    UserData.Clear();
    updateLogin(false);
    navigate("/");
  }

  return (
    <>
      {isLoggedIn ? (
        <nav>
          <ul className="topMenu">
            {recruiterMode ? (
              <>
                <li>
                  <NavLink to="/recruiter" className={({ isActive }) => (isActive ? "active" : undefined)}>
                    Hiring
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/recruit" className={({ isActive }) => (isActive ? "active" : undefined)}>
                    Companies
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/connections" className={({ isActive }) => (isActive ? "active" : undefined)}>
                    Connections
                    {(() => {
                      const n = Number(userData?.unreadChatCount ?? 0) || 0;
                      if (n <= 0) return null;
                      return (
                        <span className="navBadge" aria-label={`${n} unread chats`}>
                          {n > 99 ? "99+" : n}
                        </span>
                      );
                    })()}
                  </NavLink>
                </li>
              </>
            ) : (
              <>
                <li>
                  <NavLink to="/dashboard" className={({ isActive }) => (isActive ? "active" : undefined)}>
                    Dashboard
                    {(() => {
                      const n = Number(userData?.openNoticeCount ?? 0) || 0;
                      if (n <= 0) return null;
                      return (
                        <span className="navBadge" aria-label={`${n} open notices`}>
                          {n > 99 ? "99+" : n}
                        </span>
                      );
                    })()}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/getting-started" className={({ isActive }) => (isActive ? "active" : undefined)}>
                    Get Started
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/careers" className={({ isActive }) => (isActive ? "active" : undefined)}>
                    Careers
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/skills" className={({ isActive }) => (isActive ? "active" : undefined)}>
                    Skills
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/employments" className={({ isActive }) => (isActive ? "active" : undefined)}>
                    Employment
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/connections" className={({ isActive }) => (isActive ? "active" : undefined)}>
                    Connections
                    {(() => {
                      const n = Number(userData?.unreadChatCount ?? 0) || 0;
                      if (n <= 0) return null;
                      return (
                        <span className="navBadge" aria-label={`${n} unread chats`}>
                          {n > 99 ? "99+" : n}
                        </span>
                      );
                    })()}
                  </NavLink>
                </li>
                {forums.map((forum) => (
                  <li key={forum.skillId} className="skillForumTab" data-testid="skill-forum-tab">
                    <NavLink
                      to={`/skillForum/${forum.skillId}`}
                      title={`Skill Forum: ${forum.skillName}`}
                      className={({ isActive }) => (isActive ? "active" : undefined)}
                      onClick={() => setActiveSkillForum(forum.skillId)}
                    >
                      <span className="skillForumTabLabel">Skill Forum: {forum.skillName}</span>
                    </NavLink>
                    <button
                      type="button"
                      className="skillForumTabClose"
                      aria-label={`Close skill forum ${forum.skillName}`}
                      data-testid="skill-forum-tab-close"
                      onClick={(e) => {
                        e.preventDefault();
                        const closedId = forum.skillId;
                        const path = window.location.pathname;
                        const onClosedForum =
                          path === `/skillForum/${closedId}` ||
                          path.startsWith(`/skillForum/${closedId}/`);
                        closeSkillForum(closedId);
                        if (!onClosedForum) return;
                        const remaining = getOpenSkillForums();
                        if (remaining.length > 0) {
                          const next = remaining[remaining.length - 1];
                          navigate(`/skillForum/${next.skillId}`);
                        } else {
                          navigate("/skills");
                        }
                      }}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </>
            )}
            {showAdmin ? (
              <li>
                <NavLink to="/admin" className={({ isActive }) => (isActive ? "active" : undefined)}>
                  Admin
                </NavLink>
              </li>
            ) : null}
            <li className="topMenuActions">
              {showHiring ? <ModeSwitch /> : null}
              <ThemeToggle />
              <NavLink
                to="/account"
                className={({ isActive }) => "topMenuIcon" + (isActive ? " active" : "")}
                aria-label="Account"
                title="Account"
              >
                <FaUser aria-hidden="true" />
              </NavLink>
              <NavLink
                to="/"
                onClick={onLogout}
                className="topMenuIcon"
                aria-label="Log out"
                title="Log out"
              >
                <FiLogOut aria-hidden="true" />
              </NavLink>
            </li>
          </ul>
        </nav>
      ) : (
        <nav>
          <ul className="topMenu">
            <li>
              <NavLink to="/" className={({ isActive }) => (isActive ? "active" : undefined)}>
                Login
              </NavLink>
            </li>
            <li>
              <NavLink to="/register" className={({ isActive }) => (isActive ? "active" : undefined)}>
                Register
              </NavLink>
            </li>
            <li>
              <NavLink to="/about" className={({ isActive }) => (isActive ? "active" : undefined)}>
                About
              </NavLink>
            </li>
            <li>
              <NavLink to="/faq" className={({ isActive }) => (isActive ? "active" : undefined)}>
                FAQ
              </NavLink>
            </li>
            <li>
              <NavLink to="/contact" className={({ isActive }) => (isActive ? "active" : undefined)}>
                Contact
              </NavLink>
            </li>
          </ul>
        </nav>
      )}
    </>
  );
}
